import { CanActivateFn, Router } from '@angular/router';
import { inject } from '@angular/core';
import { map, take } from 'rxjs/operators';
import { PermissionsService } from '../services/permissions.service';
import { PermissionAction } from '../permissions/permission-actions.enum';
import { PAGE_REGISTRY_BY_KEY } from '../permissions/page-registry';

// Replacement for the inline `ngxPermissionsGuard` + `data.permissions.only` pattern. Reads
// `route.data.pageKey` (required) and `route.data.requiredAction` (optional, defaults to
// View), then defers to PermissionsService.can$().
//
// Cutover bridge: if the role-based check denies, the guard *also* accepts the legacy admin
// flag recorded on the page-registry entry's `legacyPermission`. That way an existing
// CorporateAdmin who hasn't been assigned a role yet keeps the same access they had before —
// no flash of redirects on first deploy. Phase 7 deletes the bridge once admins have been
// migrated.
//
// Usage in routes:
//   { path: 'reports', canActivate: [MsalGuard, organizationAccessGuard, pagePermissionGuard],
//     data: { pageKey: 'corporate.reports' } }
export const pagePermissionGuard: CanActivateFn = (route) => {
  const permissionsService = inject(PermissionsService);
  const router = inject(Router);

  const pageKey = route.data['pageKey'] as string | undefined;
  if (!pageKey) {
    // Misconfigured route — fail closed and log. A guard with no pageKey is almost always a
    // copy-paste mistake, not an intentional "allow everyone" gate.
    // eslint-disable-next-line no-console
    console.error('[pagePermissionGuard] route is missing data.pageKey');
    router.navigate(['']);
    return false;
  }
  const action = (route.data['requiredAction'] as PermissionAction | undefined) ?? PermissionAction.View;

  return permissionsService.can$(pageKey, action).pipe(
    take(1),
    map(roleBased => {
      const surface = PAGE_REGISTRY_BY_KEY.get(pageKey);
      const legacyAllowed = surface?.legacyPermission
        ? permissionsService.hasLegacyFlag(surface.legacyPermission)
        : null;

      // Cutover diagnostic. Every route activation that ends up taking the legacy bridge gets
      // logged exactly once with the page key + flag — when these stop appearing in dev/staging
      // logs over a release cycle, it's safe to delete the legacyPermission fallback (Phase 7
      // sunset). We log only the asymmetric case (legacy allowed but role didn't) because the
      // reverse "role allowed and legacy didn't" can't strand a user — we'd already let them in.
      if (!roleBased && legacyAllowed === true) {
        // eslint-disable-next-line no-console
        console.info('[permissions.cutover] legacy bridge granted access', {
          pageKey,
          legacyFlag: surface?.legacyPermission,
          action,
        });
      }

      if (roleBased || legacyAllowed === true) return true;

      router.navigate(['']);
      return false;
    })
  );
};
